/**
 * Input validation and sanitization utilities
 *
 * IMPORTANT: Always sanitize user input before:
 * - Storing in database
 * - Displaying in UI
 * - Using in API requests
 */

/**
 * Sanitize text input by removing potentially dangerous characters
 * Use for general text fields, names, etc.
 */
export const sanitizeText = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') return ''

  return input
    .trim()
    .slice(0, maxLength)
    // Remove null bytes
    .replace(/\0/g, '')
    // Remove most control characters (keep newlines and tabs)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
}

/**
 * Sanitize HTML by stripping all tags
 * Use when you want plain text only
 */
export const sanitizeHTML = (input: string): string => {
  if (!input || typeof input !== 'string') return ''

  return input
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&amp;/g, '&')
}

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false

  // RFC 5322 simplified regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return emailRegex.test(email) && email.length <= 254
}

/**
 * Sanitize email (trim and lowercase)
 */
export const sanitizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return ''

  return email.trim().toLowerCase().slice(0, 254)
}

/**
 * Validate URL format
 */
export const validateUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false

  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Sanitize URL - ensures it starts with http:// or https://
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return ''

  const trimmed = url.trim()

  // Check for javascript: protocol and other dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:']
  const lowerUrl = trimmed.toLowerCase()

  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return ''
    }
  }

  // Add https:// if no protocol
  if (!trimmed.match(/^https?:\/\//i)) {
    return `https://${trimmed}`
  }

  return trimmed
}

/**
 * Validate phone number (basic validation for US numbers)
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  // US phone numbers: 10 digits (with optional +1 prefix = 11 digits)
  return digits.length === 10 || (digits.length === 11 && digits[0] === '1')
}

/**
 * Sanitize phone number - keeps only digits and formatting characters
 */
export const sanitizePhone = (phone: string): string => {
  if (!phone || typeof phone !== 'string') return ''

  return phone.replace(/[^\d\s()\-+]/g, '').trim()
}

/**
 * Validate string length
 */
export const validateLength = (
  input: string,
  min: number = 0,
  max: number = Infinity
): boolean => {
  if (!input || typeof input !== 'string') return min === 0

  return input.length >= min && input.length <= max
}

/**
 * Validate number is within range
 */
export const validateNumberRange = (
  value: number,
  min: number = -Infinity,
  max: number = Infinity
): boolean => {
  if (typeof value !== 'number' || isNaN(value)) return false

  return value >= min && value <= max
}

/**
 * Sanitize object keys (for user-provided keys in objects)
 * Prevents prototype pollution
 */
export const sanitizeObjectKey = (key: string): string => {
  if (!key || typeof key !== 'string') return ''

  // Prevent prototype pollution
  const dangerous = ['__proto__', 'constructor', 'prototype']
  if (dangerous.includes(key.toLowerCase())) {
    return ''
  }

  // Remove special characters except alphanumeric, underscore, and hyphen
  return key.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 100)
}

/**
 * Validate alphanumeric (letters and numbers only)
 */
export const validateAlphanumeric = (input: string): boolean => {
  if (!input || typeof input !== 'string') return false

  return /^[a-zA-Z0-9]+$/.test(input)
}

/**
 * Sanitize for use in JSON
 * Removes characters that could break JSON parsing
 */
export const sanitizeForJSON = (input: string): string => {
  if (!input || typeof input !== 'string') return ''

  return input
    .replace(/\\/g, '\\\\')  // Escape backslashes
    .replace(/"/g, '\\"')    // Escape quotes
    .replace(/\n/g, '\\n')   // Escape newlines
    .replace(/\r/g, '\\r')   // Escape carriage returns
    .replace(/\t/g, '\\t')   // Escape tabs
}

/**
 * Validate array of strings
 */
export const validateStringArray = (
  arr: unknown,
  maxLength: number = 100,
  maxItemLength: number = 500
): arr is string[] => {
  if (!Array.isArray(arr)) return false
  if (arr.length > maxLength) return false

  return arr.every(item =>
    typeof item === 'string' && item.length <= maxItemLength
  )
}

/**
 * Sanitize array of strings
 */
export const sanitizeStringArray = (
  arr: unknown[],
  maxLength: number = 100,
  maxItemLength: number = 500
): string[] => {
  if (!Array.isArray(arr)) return []

  return arr
    .filter(item => typeof item === 'string')
    .map(item => sanitizeText(item, maxItemLength))
    .filter(item => item.length > 0)
    .slice(0, maxLength)
}

/**
 * Comprehensive input sanitizer - use this as default for user inputs
 */
export const sanitizeUserInput = (
  input: string,
  options: {
    maxLength?: number
    allowHTML?: boolean
    trimWhitespace?: boolean
  } = {}
): string => {
  const {
    maxLength = 1000,
    allowHTML = false,
    trimWhitespace = true
  } = options

  if (!input || typeof input !== 'string') return ''

  let sanitized = input

  // Remove HTML if not allowed
  if (!allowHTML) {
    sanitized = sanitizeHTML(sanitized)
  }

  // Trim whitespace if requested
  if (trimWhitespace) {
    sanitized = sanitized.trim()
  }

  // Apply text sanitization
  sanitized = sanitizeText(sanitized, maxLength)

  return sanitized
}

/**
 * Validate password strength
 */
export const validatePasswordStrength = (password: string): {
  valid: boolean
  errors: string[]
  score: number // 0-4
} => {
  const errors: string[] = []
  let score = 0

  if (!password || typeof password !== 'string') {
    return { valid: false, errors: ['Password is required'], score: 0 }
  }

  // Minimum length
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters long')
  } else {
    score++
  }

  // Check for uppercase
  if (/[A-Z]/.test(password)) score++
  else errors.push('Password should include uppercase letters')

  // Check for lowercase
  if (/[a-z]/.test(password)) score++
  else errors.push('Password should include lowercase letters')

  // Check for numbers
  if (/\d/.test(password)) score++
  else errors.push('Password should include numbers')

  // Check for special characters
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++
  else errors.push('Password should include special characters')

  // Common patterns to avoid
  const commonPatterns = [
    /12345/,
    /password/i,
    /qwerty/i,
    /abc123/i,
    /letmein/i
  ]

  if (commonPatterns.some(pattern => pattern.test(password))) {
    errors.push('Password contains common patterns')
    score = Math.max(0, score - 1)
  }

  return {
    valid: errors.length === 0,
    errors,
    score: Math.min(4, score)
  }
}

/**
 * Rate limiting helper (client-side)
 * Prevents rapid-fire requests
 */
const rateLimitMap = new Map<string, number>()

export const checkRateLimit = (
  key: string,
  maxRequests: number = 5,
  windowMs: number = 60000 // 1 minute
): boolean => {
  const now = Date.now()
  const lastRequest = rateLimitMap.get(key) || 0

  // Clear old entries periodically
  if (rateLimitMap.size > 1000) {
    rateLimitMap.clear()
  }

  if (now - lastRequest < windowMs / maxRequests) {
    return false // Rate limit exceeded
  }

  rateLimitMap.set(key, now)
  return true // Request allowed
}
